import { RemoteAuxiliaryTrack, RemoteVideoTrack } from './track/remote-video-track';
import { RemoteAudioTrack } from './track/remote-audio-track';
import { FSM } from 'afsm';
import EventEmitter from 'eventemitter3';
import Logger from './utils/log/logger';
import RtcError from './common/rtc-error';
import { Scene, PEER_LEAVE_REASON } from './common/constants';
import KeyPointManager from './manager/key-point-manager';
import CallDurationCalculator from './metrics/call-duration-calculator';
import BadCaseDetector from './metrics/bad-case-detector';
import { AudioManager } from './manager/audio-manager';
import { VideoManager } from './manager/video-manager';
import { AudioSource } from './manager/audio-mixer';
export const enum FrameWorkType {
  WEBRTC = 30, // WebRTC
  WASM = 37, // WebAssembly
}
/*
 * Connection's Event
 */
export const enum ConnectionEvent {
  TRACK_ADDED = 'track-added',
  TRACK_UPDATED = 'track-updated',
  TRACK_SUBSCRIBED = 'track-subscribed',
  STREAM_ADDED = 'stream-added',
  STREAM_REMOVED = 'stream-removed',
  STREAM_UPDATED = 'stream-updated',
  STREAM_PUBLISHED = 'stream-published',
  STREAM_SUBSCRIBED = 'stream-subscribed',
  STREAM_UNSUBSCRIBED = 'stream-unsubscribed',
  STATE_CHANGED = 'state-changed',
  ERROR = 'error',
  CONNECTION_STATE_CHANGED = 'connection-state-changed',
  FIREWALL_RESTRICTION = 'firewall-restriction',
  SEI_MESSAGE = 'sei-message',
  CLOSED = 'closed',
}

export const enum CONNECTION_CLOSED_REASON {
  REMOTE_LEAVE = 'remote user exitRoom',
  REMOTE_UNPUBLISH = 'remote user unpublished',
  LOCAL_LEAVE = 'you exitRoom',
  LOCAL_UNPUBLISH = 'you unpublished',
  LOCAL_UNSUBSCRIBE = 'you unsubscribed',
  SWITCH_ROLE = 'you switch role to audience'
}

/**
 * **客户端事件列表**<br>
 * <br>
 * `Client.on('eventName')` 事件监听中的事件名称。
 * @module ClientEvent
 */
export const enum ClientEvent {
  /**
   * @description 远端流添加事件，当远端用户发布流后会收到该通知。
   * @default 'stream-added'
   * @memberof module:ClientEvent
   * @example
   * client.on('stream-added', event => {
   *   const remoteStream = event.stream;
   * });
   */
  STREAM_ADDED = 'stream-added',
  /**
   * @description 远端流移除事件，当远端用户取消发布流后会收到该通知。
   * @default 'stream-removed'
   * @memberof module:ClientEvent
   * @example
   * client.on('stream-removed', event => {
   *   const remoteStream = event.stream;
   * });
   */
  STREAM_REMOVED = 'stream-removed',
  /**
   * @description 远端流更新事件，当远端用户添加、移除或更换音视频轨道后会收到该通知。
   * @default 'stream-updated'
   * @memberof module:ClientEvent
   * @example
   * client.on('stream-updated', event => {
   *   const remoteStream = event.stream;
   * });
   */
  STREAM_UPDATED = 'stream-updated',
  /**
   * @description 远端流订阅成功事件，调用 {@link Client#subscribe subscribe()} 成功后会触发该事件。
   * @default 'stream-subscribed'
   * @memberof module:ClientEvent
   * @example
   * client.on('stream-subscribed', event => {
   *   const remoteStream = event.stream;
   * });
   */
  STREAM_SUBSCRIBED = 'stream-subscribed',
  /**
   * @description SDK 和腾讯云的连接状态变更事件，您可以利用该事件从总体上监听 SDK 与腾讯云的连接状态。<br>
   * 若您需要细分监听每一条 Stream 的连接状态，可以监听 {@link module:StreamEvent.CONNECTION_STATE_CHANGED StreamEvent.CONNECTION_STATE_CHANGED} 事件处理。
   * - 'DISCONNECTED'：连接断开
   * - 'CONNECTING'：正在连接中
   * - 'CONNECTED'：已连接
   * - 'RECONNECTING'：自动重连中
   *
   * 不同状态变更的含义：
   *
   * - DISCONNECTED -> CONNECTING: 正在尝试建立连接，调用进房接口时触发。
   * - CONNECTING -> CONNECTED: 连接建立成功，调用进房接口， Websocket 连接成功时触发
   * - CONNECTED -> DISCONNECTED: 连接中断，当网络异常导致连接断开时触发。
   * - DISCONNECTED -> RECONNECTING: 正在重连，当连接异常断开时，SDK 尝试自动重连时触发。
   * - RECONNECTING -> CONNECTED: SDK 自动重连成功。
   * - RECONNECTING -> DISCONNECTED: SDK 自动重连失败。同时会抛出 client.on('error') 事件，错误码为 {@link module:ErrorCode.SIGNAL_CHANNEL_RECONNECTION_FAILED SIGNAL_CHANNEL_RECONNECTION_FAILED}。
   *
   * 处理建议：可以监听该事件，在不同状态显示不同的 UI，提醒用户当前的连接状态。
   *
   * @default 'connection-state-changed'
   * @memberof module:ClientEvent
   * @example
   * client.on('connection-state-changed', event => {
   *   const prevState = event.prevState;
   *   const curState = event.state;
   * });
   */
  CONNECTION_STATE_CHANGED = 'connection-state-changed',
  /**
   * @description 远端用户进房通知
   *
   * **注意：**
   * - live 模式下，不支持观众进退房通知
   * - v4.8.2 之前版本，远端用户进房推流后，才会收到进房通知<br/>
   * v4.8.2 及其之后版本，远端用户进房，就能收到进房通知
   * @default 'peer-join'
   * @memberof module:ClientEvent
   * @example
   * client.on('peer-join', event => {
   *   const userId = event.userId;
   * });
   */
  PEER_JOIN = 'peer-join',
  /**
   * @description 远端用户退房通知
   *
   * **注意：**
   * - live 模式下，不支持观众进退房通知
   * - v4.8.2 之前版本，当远端用户取消推流或退房时，都会通知退房<br/>
   * v4.8.2 及其之后版本，只有远端用户退房时，才会通知退房
   * @default 'peer-leave'
   * @memberof module:ClientEvent
   * @example
   * client.on('peer-leave', event => {
   *   const userId = event.userId;
   * });
   */
  PEER_LEAVE = 'peer-leave',
  /**
   * @description 远端用户禁用音频通知。
   * @default 'mute-audio'
   * @memberof module:ClientEvent
   * @example
   * client.on('mute-audio', event => {
   *   const userId = event.userId;
   * });
   */
  MUTE_AUDIO = 'mute-audio',
  /**
   * @description 远端用户禁用视频通知。
   * @default 'mute-video'
   * @memberof module:ClientEvent
   * @example
   * client.on('mute-video', event => {
   *   const userId = event.userId;
   * });
   */
  MUTE_VIDEO = 'mute-video',
  /**
   * @description 远端用户启用音频通知。
   * @default 'unmute-audio'
   * @memberof module:ClientEvent
   * @example
   * client.on('unmute-audio', event => {
   *   const userId = event.userId;
   * });
   */
  UNMUTE_AUDIO = 'unmute-audio',
  /**
   * @description 远端用户启用视频通知。
   * @default 'unmute-video'
   * @memberof module:ClientEvent
   * @example
   * client.on('unmute-video', event => {
   *   const userId = event.userId;
   * });
   */
  UNMUTE_VIDEO = 'unmute-video',
  /**
   * @description 被动退出房间事件。从 v4.14.0 版本开始，事件回调返回退房原因（reason）和对应的原因描述（message），reason 枚举值及含义如下：
   *  | 值 | 含义 |
   *  | :--- | :---- |
   *  | kick | 同名用户同时进入同一个房间，先进入房间的用户会被后进入房间的用户踢出房间 <br> **注意：同名用户同时登录同一房间是不允许的行为，可能会导致双方音视频通话异常，业务侧应避免出现这种情况。** |
   *  | banned | 系统管理员通过{@link https://cloud.tencent.com/document/product/647/40496 服务端 API} 将该用户踢出房间 |
   *  | room_disband | 系统管理员通过{@link https://cloud.tencent.com/document/product/647/40496 服务端 API} 解散房间 |
   *
   * **Note:**
   * - 用户的角色如果是观众，同名用户同时进入同一个房间，可能会收不到该事件。
   * - 建议您使用 {@link https://web.sdk.qcloud.com/im/doc/zh-cn/module-EVENT.html#.KICKED_OUT WebIM SDK KICKED_OUT} 或者业务侧的登录态，来避免同名用户登陆同一房间。
   * @default 'client-banned'
   * @memberof module:ClientEvent
   * @example
   * // v4.14.0 及其之后的版本
   * client.on('client-banned', event => {
   *   console.log(`reason: ${event.reason}, message: ${event.message}`);
   *   // 根据对应的 reason 提示用户
   * });
   * // v4.14.0 之前的版本
   * client.on('client-banned', error => {
   *   console.error('client-banned observed: ' + error);
   *   // 提示退房
   * });
   */
  CLIENT_BANNED = 'client-banned',
  /**
   * @since v4.6.0
   * @description 网络质量统计数据事件，进房后开始统计，每两秒触发一次，包括上、下行的网络质量数据。
   * - 上行网络质量（uplinkNetworkQuality）为 SDK 到腾讯云的上行连接网络质量
   * - 下行网络质量（downlinkNetworkQuality）为腾讯云到 SDK 的所有下行连接的平均网络质量
   *    其枚举值及含义如下表所示：
   *    | 数值 | 含义 |
   *    | :--- | :---- |
   *    | 0 | 网络状况未知，表示当前 client 实例还没有建立上行/下行连接 |
   *    | 1 | 网络状况极佳 |
   *    | 2 | 网络状况较好|
   *    | 3 | 网络状况一般 |
   *    | 4 | 网络状况差 |
   *    | 5 | 网络状况极差 |
   *    | 6 | 网络连接已断开<br/>注意：若下行网络质量为此值，则表示所有下行连接都断开了 |
   * 自 v4.10.3 版本开始，支持获取上、下行的 RTT 及丢包率
   * - uplinkRTT，uplinkLoss 为上行 RTT(ms) 及上行丢包率。
   * - downlinkRTT，downlinkLoss 为所有下行连接的平均 RTT(ms) 及平均丢包率。
   * @default 'network-quality'
   * @memberof module:ClientEvent
   * @example
   * client.on('network-quality', event => {
   *    console.log(`network-quality, uplinkNetworkQuality:${event.uplinkNetworkQuality}, downlinkNetworkQuality: ${event.downlinkNetworkQuality}`)
   *
   *    // 自 v4.10.3 支持获取上、下行的 RTT 及丢包率
   *    console.log(`uplink rtt:${event.uplinkRTT} loss:${event.uplinkLoss}`)
   *    console.log(`downlink rtt:${event.downlinkRTT} loss:${event.downlinkLoss}`)
   * })
   */
  NETWORK_QUALITY = 'network-quality',
  /**
   * @description 音量大小事件<br>
   * 调用 {@link Client#enableAudioVolumeEvaluation enableAudioVolumeEvaluation} 接口开启音量大小回调后，SDK 会定时抛出该事件，通知每个 userId 的音量大小。<br>
   * **Note**
   * - 回调中包含推流的本地流及已订阅的远端流的音量大小，无论是否有人说话，都会触发该回调。
   * - audioVolume 取值为0-100的正整数，通常认为 audioVolume > 10 的用户正在说话
   * @since v4.9.0
   * @default 'audio-volume'
   * @memberof module:ClientEvent
   * @example
   * client.on('audio-volume', event => {
   *     event.result.forEach(({ userId, audioVolume, stream }) => {
   *         if (audioVolume > 10) {
   *            console.log(`user: ${userId} is speaking, audioVolume: ${audioVolume}`);
   *         }
   *     })
   * })
   *
   * // 开启音量回调，并设置每 1000ms 触发一次事件
   * client.enableAudioVolumeEvaluation(1000);
   */
  AUDIO_VOLUME = 'audio-volume',
  /**
   * @description 收到 SEI message<br>
   * @since v4.14.1
   * @default 'sei-message'
   * @memberof module:ClientEvent
   * @example
   * client.on('sei-message', event => {
   *    console.log(`收到 ${event.userId} 的 sei message: ${event.data}`)
   * })
   */
  SEI_MESSAGE = 'sei-message',
  /**
   * @description 错误事件，当出现不可恢复错误后，会抛出此事件
   * @default 'error'
   * @memberof module:ClientEvent
   * @example
   * client.on('error', error => {
   *   console.error('client error observed: ' + error);
   *   const errorCode = error.getCode();
   *   // 请根据错误码列表(https://cloud.tencent.com/document/product/647/34342)查看错误类型。
   *   // 当出现客户端错误后，请调用 Client.leave() 退房并尝试通过 Client.join() 重新进房恢复通话。
   * });
   *
   * stream.on('error', error => {
   *   const errorCode = error.getCode();
   *   if (errorCode === 0x4043) {
   *     // 自动播放受限，引导用户手势操作并调用 stream.resume 恢复音视频播放
   *     // 参考：https://web.sdk.qcloud.com/trtc/webrtc/doc/zh-cn/module-ErrorCode.html#.PLAY_NOT_ALLOWED
   *   } else if (errorCode === 0x4044) {
   *     // 媒体设备被拔出后自动恢复失败，参考：https://web.sdk.qcloud.com/trtc/webrtc/doc/zh-cn/module-ErrorCode.html#.DEVICE_AUTO_RECOVER_FAILED
   *   }
   * });
   */
  ERROR = 'error',
}

/**
 * **Stream 事件列表**<br>
 * <br>
 * `Stream.on('eventName')` 事件监听中的事件名称。
 * @module StreamEvent
 */
export const enum StreamEvent {
  /**
   * @description Audio/Video Player 状态变化事件
   * App 可根据状态变化来更新 UI，比如，通过监听 video player 状态变化来关闭或打开遮板。
   * - 'PLAYING'：开始播放
   * - 'PAUSED'：暂停播放
   *   - event.reason 为 'pause' 时，由 \<audio\>/\<video\> element 的 pause 事件触发，如下几种情况会触发：
   *      - 调用 HTMLMediaElement.pause 接口。
   *      - 在 stream.play(elementId) 后，从 DOM 中移除 elementId 对应的 div 容器。
   *      - 在 stream.play(elementId) 后，移动 elementId 对应的 div 容器，在 Chrome M70 版本及以下的浏览器上可能会触发该事件，此时您需要调用 Stream.resume() 继续播放。
   *   - event.reason 为 'mute' 时。详见事件 {@link https://developer.mozilla.org/en-US/docs/Web/API/MediaStreamTrack/mute_event | MediaStreamTrack.mute_event}
   *      - 若 LocalStream 触发该事件，表明音频/视频采集暂停，通常是设备异常引起，如设备被其他应用抢占，此时需引导用户重新采集。
   *      - 若 RemoteStream 触发该事件，表明收到的音频/视频数据不足以播放。通常是网络抖动引起，接入侧无需做任何处理。当收到的数据足以播放时，会自动恢复。
   * - 'STOPPED'：停止播放
   *
   * reason 状态变化原因：
   * - 'playing'：开始播放，详见事件 {@link https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/playing_event | HTMLMediaElement.playing_event}
   * - 'mute'：音视频轨道暂时未能提供数据，详见事件 {@link https://developer.mozilla.org/en-US/docs/Web/API/MediaStreamTrack/mute_event | MediaStreamTrack.mute_event}
   * - 'unmute'：音视频轨道恢复提供数据，详见事件 {@link https://developer.mozilla.org/en-US/docs/Web/API/MediaStreamTrack/unmute_event | MediaStreamTrack.unmute_event}
   * - 'ended'：音视频轨道已被关闭
   * - 'pause'：播放暂停
   * @default 'player-state-changed'
   * @memberof module:StreamEvent
   * @example
   * stream.on('player-state-changed', event => {
   *   console.log(`${event.type} player is ${event.state} because of ${event.reason}`);
   * });
   */
  PLAYER_STATE_CHANGED = 'player-state-changed',

  /**
   * @description 本地屏幕分享停止事件通知，仅对本地屏幕分享流有效。
   * @default 'screen-sharing-stopped'
   * @memberof module:StreamEvent
   * @example
   * stream.on('screen-sharing-stopped', () => {
   *   console.log('screen sharing was stopped');
   * });
   */
  SCREEN_SHARING_STOPPED = 'screen-sharing-stopped',

  /**
   * @description Stream 连接状态变更事件
   * - 'DISCONNECTED'：连接断开
   * - 'CONNECTING'：正在连接中
   * - 'CONNECTED'：已连接
   * - 'RECONNECTING'：自动重连中
   *
   *
   * 不同状态变更的含义：
   * - DISCONNECTED -> CONNECTING: 正在尝试建立连接，调用推流或者订阅接口但尚未成功时触发。
   * - CONNECTING -> CONNECTED: 连接建立成功，推流成功或订阅成功触发。
   * - CONNECTED -> DISCONNECTED: 连接中断，当网络异常导致连接断开时触发。
   * - DISCONNECTED -> RECONNECTING: 正在重连，当连接异常断开时，SDK 尝试自动重连时触发。
   * - RECONNECTING -> CONNECTED: SDK 自动重连成功。
   * - RECONNECTING -> DISCONNECTED: SDK 自动重连失败。
   *
   * 处理建议：可以监听该事件，准确区分出每路 Stream 的连接状态。在恰当时机给用户做提示。<br/>
   * 例如：当出现 RECONNECTING 时，提示用户当前连接状态异常，正在进行重连，引导用户检查自身网络是否正常。
   *
   * **Note**:
   * - 若您需要监听 Stream 的连接状态变更，请在 stream-added 事件监听该事件，并在 stream-removed 事件取消该事件监听。
   * @since v4.10.1
   * @default 'connection-state-changed'
   * @memberof module:StreamEvent
   * @example
   * stream.on('connection-state-changed', (event) => {
   *   console.log(`prevState: ${event.prevState}, state: ${event.state}`);
   * });
   */
  CONNECTION_STATE_CHANGED = 'connection-state-changed',
  /**
   * 当正在使用的摄像头、麦克风采集异常时，SDK 会尝试自动恢复采集。当自动恢复采集成功时，会通知该事件。否则 SDK 会抛出错误 {@link module:ErrorCode.DEVICE_AUTO_RECOVER_FAILED DEVICE_AUTO_RECOVER_FAILED}。
   * @since v4.13.0
   * @default 'device-auto-recovered'
   * @memberof module:StreamEvent
   * @example
   * localStream.on('device-auto-recovered', (event) => {
   *  // 当前事件是否恢复了摄像头、麦克风的采集，可能同时为 true。
   *  console.warn(event.isCamera, event.isMicrophone);
   *  // 恢复采集后，当前 localStream 使用的摄像头、麦克风 deviceId。
   *  console.warn(event.cameraId, event.microphoneId);
   * });
   */
  DEVICE_AUTO_RECOVERED = 'device-auto-recovered',
  /**
   * @description 错误事件，当出现不可恢复错误后，会抛出此事件
   * @default 'error'
   * @memberof module:StreamEvent
   * @example
   * stream.on('error', error => {
   *   const errorCode = error.getCode();
   *   if (errorCode === 0x4043) {
   *     // 自动播放受限，引导用户手势操作并调用 stream.resume 恢复音视频播放
   *     // 参考：https://trtc-1252463788.file.myqcloud.com/web/docs/module-ErrorCode.html#.PLAY_NOT_ALLOWED
   *   } else if (errorCode === 0x4044) {
   *     // 媒体设备被拔出后自动恢复失败，参考：https://trtc-1252463788.file.myqcloud.com/web/docs/module-ErrorCode.html#.DEVICE_AUTO_RECOVER_FAILED
   *   }
   * });
   */
  ERROR = 'error',
}

export const enum LocalTrackEvent {
  DEVICE_AUTO_RECOVERED = '1',
  DEVICE_RECOVER_FAILED = '5',
  DEVICE_CHANGED = '2',
  ERROR = '3',
  PUBLISH_STATE_CHANGED = '4',
  ENCODE_FAILED = '6',
  TRACK_ENDED = '7',
  RENDER = 'render',
}

export const enum PlayerState {
  PAUSED = 'PAUSED',
  PLAYING = 'PLAYING',
  STOPPED = 'STOPPED',
}

export const enum RoomEvent {
  PEER_JOIN = 'peer-join',
  PEER_LEAVE = 'peer-leave',
  SIGNAL_CONNECTION_STATE_CHANGED = 'signal-connection-state-changed',
  MEDIA_CONNECTION_STATE_CHANGED = 'media-connection-state-changed',
  BANNED = 'banned',
  NETWORK_QUALITY = 'network-quality',
  AUDIO_VOLUME = 'audio-volume',
  SEI_MESSAGE = 'sei-message',
  ERROR = 'error',
  REMOTE_PUBLISH_STATE_CHANGED = 'remote-publish-state-changed',
  REMOTE_PUBLISHED = 'remote-published',
  REMOTE_UNPUBLISHED = 'remote-unpublished',
  FIREWALL_RESTRICTION = ConnectionEvent.FIREWALL_RESTRICTION,
  HEARTBEAT_REPORT = 'heartbeat-report',
  CUSTOM_MESSAGE = 'custom-message',
  LAYER_DATA = 'layerData', // voov 鼠标信息
  FIRST_VIDEO_FRAME = 'first-video-frame',
  DUMP = 'dump',
  AUDIO_FRAME = 'audio-frame',
  SUBSCRIBE_SMALL_VIDEO_CHANGED = 'subscribe-small-video-changed',
  LOCAL_PUBLISH_FLAG_CHANGED = 'local-publish-flag-changed',
  NTP_TIME_UPDATED = 'ntp-time-updated',
  DATA_CHANNEL_MESSAGE = 'data-channel-message',
  ASR_ROBOT_PEER_JOIN = 'asr-robot-peer-join',
  ASR_ROBOT_PEER_LEAVE = 'asr-robot-peer-leave',
}

export const enum TrackEvent {
  PLAYER_STATE_CHANGED = 'player-state-changed',
  MUTE = 'mute',
  UNMUTE = 'unmute',
  ERROR = 'error',
  INPUT_MEDIA_TRACK_CHANGED = 'input-media-track-changed',
  OUTPUT_MEDIA_TRACK_CHANGED = 'output-media-track-changed',
  FIRST_VIDEO_FRAME = 'first-video-frame',
  VIDEO_SIZE_CHANGED = 'video-size-changed'
}

export const enum RemoteTrackEvent {
  DECODE_FAILED = 'decode-failed',
  DECODE_FAILED_DURING_CALL = 'decode-failed-during-call',
  DECODE_DOWNGRADE_STATE_CHANGED = 'decode-downgrade-state-changed',
  REMOTE_PUBLISH_CHANGED = 'remote-publish-changed',
  AUDIO_FRAME_WITH_NTP = 'audio-frame-with-ntp',
}

export interface RemoteTrackEventTypes {
  [RemoteTrackEvent.DECODE_FAILED]: [{ error: any; }];
  [RemoteTrackEvent.DECODE_FAILED_DURING_CALL]: [];
  [RemoteTrackEvent.DECODE_DOWNGRADE_STATE_CHANGED]: [event: {
    type: 'webCodecs' | 'wasm' | 'mse' | 'auto' | 'mock',
    renderer: string,
    reason: string,
    prevState: VideoDecoderDowngradeState,
    state: VideoDecoderDowngradeState;
  }];
  [RemoteTrackEvent.REMOTE_PUBLISH_CHANGED]: [boolean];
  [RemoteTrackEvent.AUDIO_FRAME_WITH_NTP]: [{
    ntp: number;
    frame: RTCEncodedAudioFrame;
    hasLeavingTag: boolean;
  }];
}

export const enum MutedFlag {
  VIDEO = 0x01,
  SMALL = 0x02,
  AUX = 0x04,
  AUDIO = 0x08,
  VIDEO_MUTE = 0x10,
  AUX_MUTE = 0x20,
  AUDIO_MUTE = 0x40,
}

/**
 * Scene defined by backend
 */
export const enum SceneNumber {
  RTC = 1,
  LIVE,
}

/**
 * UserRoleNumber defined by backend
 */
export const enum UserRoleNumber {
  ANCHOR = 20,
  AUDIENCE,
}

export const enum UserRole {
  ANCHOR = 'anchor',
  AUDIENCE = 'audience',
}

export const enum ConnectionState {
  CONNECTED = 'CONNECTED',
  DISCONNECTED = 'DISCONNECTED',
  CONNECTING = 'CONNECTING',
  RECONNECTED = 'RECONNECTED',
  RECONNECTING = 'RECONNECTING',
}

export const enum AudioDecoderDowngradeState {
  INITIALIZED = 'INITIALIZED',
  STARTING = 'STARTING',
  STARTED = 'STARTED',
  FAILED = 'FAILED',
}

export const enum VideoDecoderDowngradeState {
  INITIALIZED = 'INITIALIZED',
  STARTING = 'STARTING',
  STARTED = 'STARTED',
  FAILED = 'FAILED',
}

export const enum TRACK_KIND {
  AUDIO = 'audio',
  VIDEO = 'video',
  AUXILIARY = 'auxVideo',
}

export const enum TRACK_ACTION {
  // 新增 track
  ADD = 'add',
  // 移除 track
  REMOVE = 'remove',
}

export const enum MediaType {
  NULL,
  AUDIO = 0x01,
  AUX_VIDEO = 0x02,
  BIG_VIDEO = 0x04,
  SMALL_VIDEO = 0x08,
}

export const MediaTypeLabel = {
  [MediaType.AUDIO]: TRACK_KIND.AUDIO,
  [MediaType.AUX_VIDEO]: TRACK_KIND.AUXILIARY,
  [MediaType.BIG_VIDEO]: TRACK_KIND.VIDEO
} as const;

export const enum AudioType {
  opus = 111,
}

export const enum VideoType {
  h264 = 100,
  vp8 = 101,
}

export const enum StreamType {
  Big = 'big',
  Small = 'small',
}

export const enum RemoteStreamType {
  Main = 'main',
  Aux = 'auxiliary',
}

export const enum MULTI_VIDEO_DATA_TYPE {
  MULTI_DATA_AUDIO = 1,
  MULTI_DATA_BIG_IMG,
  MULTI_DATA_SMALL_IMG,
  MULTI_DATA_AUX_IMG = 7,
  MULTI_DATA_TYPE_BUTT = 12,
}

export const enum MonitorEventId {
  PUBLISH_VIDEO = 0x8000, // 打开视频
  PUBLISH_AUDIO, // 打开音频
  UNPUBLISH_VIDEO, // 关闭视频
  UNPUBLISH_AUDIO, // 关闭音频
  MUTE_AUDIO, // 关闭音频 (仍发送静音包)
  MUTE_VIDEO, // 关闭视频 (仍发送黑屏包)
  UNMUTE_AUDIO, // 打开音频
  UNMUTE_VIDEO, // 打开视频
  SUBSCRIBE_VIDEO, // 订阅视频
  SUBSCRIBE_AUDIO, // 订阅音频
  UNSUBSCRIBE_VIDEO, // 取消订阅视频
  UNSUBSCRIBE_AUDIO, // 取消订阅音频
  SWITCH_CAMERA, // 切换摄像头
  SWITCH_MICROPHONE, // 切换麦克风
  REPLACE_VIDEO, // 更新视频
  REPLACE_AUDIO, // 更新音频
  MUTE_REMOTE_VIDEO, // 关闭远端视频
  MUTE_REMOTE_AUDIO, // 关闭远端音频
  UNMUTE_REMOTE_VIDEO, // 打开远端视频
  UNMUTE_REMOTE_AUDIO, // 打开远端音频
  JOIN, // 进房
  LEAVE, // 退房
  SIGNAL_DISCONNECTED, // 信令通道断开
  SIGNAL_CONNECTED, // 信令通道连接成功
  TRANSPORT_UPLINK_CONNECTED, // 上行媒体传输通道连接成功
  TRANSPORT_DOWNLINK_CONNECTED, // 下行媒体传输通道连接成功
  SIGNAl_RECONNECTING, // 信令通道开始重连
  SIGNAL_RECONNECT_SUCCESS, // 信令通道重连成功
  SIGNAL_RECONNECT_FAIL, // 信令通道重连失败
  TRANSPORT_UPLINK_RECONNECTING, // 上行媒体通道开始重连
  TRANSPORT_UPLINK_RECONNECT_SUCCESS, // 上行媒体通道重连成功
  TRANSPORT_UPLINK_RECONNECT_FAIL, // 上行媒体通道重连失败
  TRANSPORT_DOWNLINK_RECONNECTING, // 下行媒体通道开始重连
  TRANSPORT_DOWNLINK_RECONNECT_SUCCESS, // 下行媒体通道重连成功
  TRANSPORT_DOWNLINK_RECONNECT_FAIL, // 下行媒体通道重连失败
  SUBSCRIBE_SMALL_VIDEO, // 订阅小流
  UNSUBSCRIBE_SMALL_VIDEO, // 取消订阅小流
  PUBLISH_AUX,
  UNPUBLISH_AUX,

  // 如下对齐 native
  DEVICE_CAPTURE = 2003, // param1: 0 采集恢复成功 2 音频恢复采集失败 6 音频采集打断（拔出麦克风）7 视频采集打断（拔出摄像头） 8 摄像头采集打断（其他原因）
  VIDEO_ENCODER = 4004, // param1: 0: 切换 SW、1: 切换 HW、2 启动 SW、3 启动 HW、4 关闭编码器； param2: 0: 264, 1: 265, 2: vp8
  VIDEO_DECODER = 4005, // param1: 0: 切换 SW、1: 切换 HW、2 启动 SW、3 启动 HW、4 关闭解码器； param2: 0: 264, 1: 265, 2: vp8
}

// 主播基本信息
export interface User {
  userId: string;
  tinyId: string;
  role: UserRole;
  fromType?: number;
}

export interface BasePublishedUser extends User {
  flag: number;
  muteState: MuteState;
  terminalType?: string;
  isRobot?: boolean;
}

// 房间内已推流的主播
export interface PublishedUser extends BasePublishedUser {
  subscribeState: SubscribeState;
  remoteAudioTrack: RemoteAudioTrack;
  remoteVideoTrack: RemoteVideoTrack;
  remoteAuxiliaryTrack: RemoteAuxiliaryTrack;
}

export interface PayloadFmtp {
  payloadType: AudioType | VideoType;
  fmtp: string;
}


export type LocalStreamOption = {
  audio: boolean;
  video: boolean;
  microphoneId?: string;
  cameraId?: string;
  facingMode?: string;
  screen?: boolean;
  screenAudio: boolean;
  audioSource: MediaStreamAudioTrack;
  videoSource: MediaStreamVideoTrack;
  screenSource: MediaStreamVideoTrack;
  echoCancellation?: boolean;
  autoGainControl?: boolean;
  noiseSuppression?: boolean;
  userId: string;
  mirror?: boolean;
};

export type RemoteStreamOption = {
  type: RemoteStreamType,
  userId: string,
  isRemote: boolean;
};

export type LocalTrackOption = {
  id: string;
  userId: string;
  // mediaType: MediaType;
  log?: ILogger;
  room?: IRoom;
};

export type LocalAVTrackOption = Omit<LocalTrackOption, 'mediaType'>;

export type RemoteAVTrackOption = {
  id: string;
  userId: string;
  log: ILogger;
  room?: IRoom;
  user?: PublishedUser;
};

export interface RoomOption {
  // client._mode 属性改为 trtcScene
  useStringRoomId?: boolean;
  tinyId?: string; // 外部传入的tinyId，会议使用
  autoReceiveAudio?: boolean;
  autoReceiveVideo?: boolean;
  logger: Logger;
  sdpSemantics?: string;
  enableAutoPlayDialog?: boolean;
  enableMultiAuxStream?: boolean;
  enableSEI?: boolean;
  enableSmallStream?: boolean;
  iceTransportPolicy?: RTCIceTransportPolicy;
  enableSPC?: boolean;
  // 退房上报使用
  frameWorkType?: number;
  component?: number;
  language?: number;
  sdkType?: number;
  encodedDataProcessingListener?: EncodedDataProcessingListener;
  enableVolumeDb?: boolean;
  enableVolumeControlInIOS?: boolean;
}

export interface PlayOption {
  objectFit?: 'contain' | 'cover' | 'fill';
  muted?: boolean;
  mirror?: boolean | string;
  volume?: number;
  canvasRender?: boolean;
  poster?: string;
  isLiveStream?: boolean;
}

export interface VideoPlayOption {
  objectFit: 'contain' | 'cover' | 'fill';
  muted: boolean;
  mirror: boolean;
}


// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface AudioPlayOption {
}
export interface IPlayer {
  isPlaying: boolean;
  isStopped: boolean;
  track: MediaStreamTrack | null;

  play(options?: PlayOption): Promise<void>;

  stop(): void;

  resume(): Promise<void>;

  setTrack?(track: MediaStreamTrack | null): void;


  on(event: string, handler: any): void;

  off(event: string, handler: any): void;

  setViewMirror?(mirror: boolean): void;

  setObjectFit?(objectFit: string): void;

  setMuted(muted: boolean): void;
}
export interface IVideoPlayer extends IPlayer {
  getElement(): HTMLVideoElement | null;
  setContainer(constainer: HTMLElement | null): void;

  setRect(width: number, height: number): void;

  getVideoFrame(): string;

  /**
   * 进入全屏模式
   */
  enterFullscreen(): Promise<void>;

  /**
   * 退出全屏模式
   */
  exitFullscreen(): Promise<void>;

  /**
   * 检查是否处于全屏状态
   */
  isFullscreen(): boolean;

  /**
   * 切换全屏状态
   */
  toggleFullscreen(): Promise<void>;

  /**
   * 进入画中画模式
   */
  enterPictureInPicture(): Promise<PictureInPictureWindow>;

  /**
   * 退出画中画模式
   */
  exitPictureInPicture(): Promise<void>;

  /**
   * 检查是否处于画中画状态
   */
  isPictureInPicture(): boolean;

  /**
   * 切换画中画状态
   */
  togglePictureInPicture(): Promise<void>;
}
export interface IAudioPlayer extends IPlayer {
  getElement(): HTMLAudioElement | null;

  setSinkId(sink: string): void;

  setVolume(volume: number): void;

  getAudioLevel(): number;
}

export const enum NetworkQualityValue {
  UNKNOWN,
  EXCELLENT,
  GOOD,
  POOR,
  BAD,
  VERY_BAD,
  DISCONNECTED
}

export declare interface PingResult {
  domain: string;
  cost: number;
}

export declare interface PingResults {
  isPoorNetwork?: boolean;
  timestamp?: number;
  data?: PingResult[];
}

export interface NetworkQuality {
  uplinkNetworkQuality: NetworkQualityValue;
  downlinkNetworkQuality: NetworkQualityValue;
  uplinkRTT: number,
  uplinkLoss: number,
  downlinkRTT: number,
  downlinkLoss: number;
  pingResults?: PingResults;
  /** 近期是否出现过差的上行网络 */
  hadRecentBadUplink?(threshold?: number): boolean;
  /** 近期是否出现过差的下行网络 */
  hadRecentBadDownlink?(threshold?: number): boolean;
}

export interface IRoomEventTypes {
  [RoomEvent.REMOTE_PUBLISH_STATE_CHANGED]: (muteStates: { preMuteState: MuteState, muteState: MuteState; }) => void;
  [RoomEvent.PEER_JOIN]: (user: User) => void;
  [RoomEvent.ASR_ROBOT_PEER_JOIN]: (user: User) => void;
  [RoomEvent.ASR_ROBOT_PEER_LEAVE]: (user: User) => void;
  [RoomEvent.PEER_LEAVE]: (event: {userId: string, reason?: keyof typeof PEER_LEAVE_REASON; }) => void;
  [RoomEvent.REMOTE_PUBLISHED]: (user: PublishedUser) => void;
  [RoomEvent.REMOTE_UNPUBLISHED]: (user: PublishedUser) => void;
  [RoomEvent.BANNED]: (event: { reason: BannedReason, message: string; }) => void;
  [RoomEvent.NETWORK_QUALITY]: (quality: NetworkQuality) => void;
  [RoomEvent.SIGNAL_CONNECTION_STATE_CHANGED]: (event: { prevState: ConnectionState, state: ConnectionState; isReconnecting?: boolean;}) => void;
  [RoomEvent.ERROR]: (error: RtcError) => void;
  [RoomEvent.AUDIO_VOLUME]: (volumes: TRTCVolumeInfo[]) => void;
  [RoomEvent.LOCAL_PUBLISH_FLAG_CHANGED]: (user: BasePublishedUser) => void;
}
export const enum ReceiveMode {
  MANUAL,
  AUTO_AUDIO,
  AUTO_VIDEO,
  AUTO_ALL
}
/** @internal */
export interface IRoom extends EventEmitter<IRoomEventTypes | EventEmitter.ValidEventTypes> {
  joinParams?: TRTCParams | null;

  localPublishFlag: number;

  proxy_ws?: string;
  seq: number;
  sdkAppId: number;
  userId: string;
  userSig: string;
  privateMapKey?: string;
  latencyLevel?: LatencyLevel;
  tinyId?: string;
  scene: Scene;
  roomId: string;
  localTracks: Set<ILocalTrack>;
  capturedLocalMainAudioTrack?: any;
  capturedLocalMainVideoTrack?: any;
  capturedLocalAuxVideoTrack?: any;
  remotePublishedUserMap: Map<string, PublishedUser>;
  useStringRoomId: boolean;
  role: UserRole;
  /** 当前用户是否有推主流 */
  isMainStreamPublished: boolean;
  /** 当前用户是否有推辅流 */
  isAuxStreamPublished: boolean;
  /** 当前房间是否存在辅流，即判断本人 or 其他人是否有推辅流 */
  hasAuxStream: boolean;
  quality?: {
    uplinkNetworkQuality: number;
    downlinkNetworkQuality: number;
    uplinkInfo: TC_UpStreamInfo;
    downlinkInfo: { rtt: number, loss: number, userId: string; }[];
    uplinkRTT: number;
    uplinkLoss: number;
    downlinkRTT: number;
    downlinkLoss: number;
  };
  scheduleResult: ScheduleResult;
  videoDecodeFallbackType?: 'auto' | 'wasm' | 'wasm_video' | 'wasm_2d';
  enableAutoPlayDialog: boolean;
  autoReceiveAudio: boolean;
  autoReceiveVideo: boolean;
  // webrtc 属性
  enableSEI?: boolean;
  audioManager: AudioManager;
  videoManager: VideoManager;
  keyPointManager: KeyPointManager;
  callDurationCalculator: CallDurationCalculator;
  badCaseDetector: BadCaseDetector;
  heartbeatReport: HearBeatReport;
  heartbeatCount: number;
  networkQuality?: NetworkQuality;
  enableChorus?: boolean;
  scriptTransformWorker?: Worker;
  playoutDelay?: PlayoutDelay;
  jitterBufferDelay?: JitterBufferDelay;
  smallMode: SMALL_MODE;
  videoCodec: VideoCodec;
  downlinkVideoCodec: VideoCodec;
  enableVolumeControlInIOS?: boolean;
  isUsingVideoMixerInternalTrack?: boolean;
  whipQuickStartConfig?: {
    remoteUserId: string;
    small?: boolean;
    domain?: string;
  };
  videoMixerOutputTrack?: MediaStreamTrack | null;
  getLogger(): Logger;

  join(params: TRTCParams, scene: Scene, frameWorkType: number): Promise<void>;

  get isJoining(): boolean;
  get isJoined(): boolean;

  leave(): Promise<void>;
  getSignalInfo(): SignalInfo;
  checkDestroy(): void;
  destroy(): void;

  switchRole(role: UserRole): Promise<void>;

  publish(localTrack: ILocalTrack): Promise<void>;

  unpublish(localTrack: ILocalTrack): Promise<void>;

  subscribe(...remoteTracks: IRemoteTrack[]): Promise<void>;

  unsubscribe(...remoteTracks: IRemoteTrack[]): Promise<void>;

  addTrack(localTrack: ILocalTrack): Promise<void>;

  removeTrack(localTrack: ILocalTrack): void;

  replaceTrack(localTrack: ILocalTrack): Promise<void>;
  enableSmall(enable: boolean): Promise<void>;

  setBandWidth?(option: BandWidthOption): Promise<unknown>;

  changeType(isSmall: boolean, user: User): void;

  setEncodedDataProcessingListener(
    listener: EncodedDataProcessingListener
  ): void;

  enableAudioVolumeEvaluation(
    interval?: number,
    enableInBackground?: boolean
  ): void;

  getLocalAudioStats(): Promise<LocalAudioStats>;

  getLocalVideoStats(): Promise<LocalVideoStats>;

  getTransportStats(): Promise<TransportStats>;

  getRemoteVideoStats(type: RemoteStreamType): Promise<RemoteVideoStats>;

  getRemoteAudioStats(): Promise<RemoteAudioStats>;

  enableAIVoice(value: boolean): void;

  setProxyServer(
    options:
      | string
      | {
        websocketProxy?: string;
        webtransportProxy?: string;
        loggerProxy?: string;
      }
  ): void;

  setTurnServer?(options: TurnServerOptions | TurnServerOptions[], iceTransportPolicy?: RTCIceTransportPolicy): void;
  sendStartMixTranscode(data: StartMixTranscodeData): Promise<{ data: { code: number, message: string; data: any; }; }>;
  sendStopMixTranscode(data: StopMixTranscodeData): Promise<{ data: { code: number, message: string; data: any; }; }>;
  sendStartPublishCDN(data: StartPublishTencentCDNData | StartPublishGivenCDNData, isTencentCDN?: boolean): Promise<{ data: { code: number, message: string; data: any; }; }>;
  sendStopPublishCDN(data: StopPublishTencentCDNData | StopPublishGivenCDNData, isTencentCDN?: boolean): Promise<{ data: { code: number, message: string; data: any; }; }>;
  sendAbilityStatus(data: AbilityStatusReportData): void;
  enableInsertableStreams(): Promise<void>;
  sendCustomMessage?(data: CustomMessageData): void;
  sendSignalMessage?(param: SendWaitForResponseWithRetryParams): Promise<SignalResponse>;
  sendStartPushStreamToRoom(data: StartPublishMixStreamToRoomData): Promise<{ data: { code: number, message: string; data: any; }; }>;
  sendUpdatePushStreamToRoom(data: UpdatePublishMixStreamToRoomData): Promise<{ data: { code: number, message: string; data: any; }; }>;
  sendStopPushStreamToRoom(data: StopPublishMixStreamToRoomData): Promise<{ data: { code: number, message: string; data: any; }; }>;
  switchRoom(options: { roomId?: number, stringRoomId?: string; userSig?: string; privateMapKey?: string; }): Promise<void>;
  isSwitchRoomSupported(): boolean;

  startUpdateNTPTime?(): Promise<void> | void;
  subscribeDataChannel(): Promise<void> | void;
  unsubscribeDataChannel(): Promise<void> | void;

  preconnect(sdkAppId: number, userId: string, userSig: string, frameWorkType: number, roomId?: number, strRoomId?: string): Promise<void>;
  closePreconnect(): Promise<void>;
}

export interface SendWaitForResponseParams {
  command: string,
  data?: any,
  timeout?: number,
  responseCommand: string,
  commandDesc?: string,
  enableLog?: boolean,
  addReceiveTime?: boolean;
}

export interface SendWaitForResponseWithRetryParams extends SendWaitForResponseParams {
  retries?: number;
  retryTimeout?: number;
}

export interface SignalResponse {
  data: {
    cmd: number;
    code: number;
    message: string;
    data: any;
    seq: number;
    tinyId: string;
    userId: string;
    expectSeq?: number;
    errorList?: { roomId: string, userId: string, message: string; }[],
    receiveTime?: number;
  };
}


export interface ILogger {
  userId?: string;
  isLocal?: boolean;
  info(...args: any[]): void;

  debug(...args: any[]): void;

  warn(...args: any[]): void;

  error(...args: any[]): void;
}
/** @internal */
export interface ITrack extends FSM {
  id: string;
  userId: string;
  isPlayCalled: boolean;
  mediaTrack: MediaStreamTrack | null; // 浏览器原生Track
  outMediaTrack: MediaStreamTrack | null; // 前处理后的Track
  kind: AudioOrVideo; // 类型：主视频，音频，辅流视频
  mediaType: MediaType; // 类型：主视频，音频，辅流视频
  streamType: RemoteStreamType;
  isRemote: boolean;
  isScreen: boolean;
  isAudio: boolean;
  muted: boolean | string;
  abortCtrl?: AbortController; // 终止信号控制
  room?: IRoom;
  player?: IPlayer; // 播放器
  container: HTMLElement | null;
  isMediaTrackActive: boolean;
  strMediaType: string;
  setMute(value: boolean): void;
  setPlayerMute(value: boolean): void;
  play(...args: any[]): void; // 开始播放
  stop(): void; // 停止播放
  close(): void; // 停止播放，停止采集，停止推拉
  get log(): ILogger;
  setInputMediaStreamTrack(track: MediaStreamTrack): void | Promise<void> | Promise<any>;
  setOutputMediaStreamTrack(track: MediaStreamTrack): void | Promise<void>;
  updatePlayingState(has: boolean): void;
}

export enum FacingMode {
  user = 'user',
  environment = 'environment'
}

// @ts-ignore
export interface GetUserMediaOption extends MediaTrackConstraintSet {
  audio: boolean,
  video: boolean,
  width?: number;
  height?: number;
  frameRate?: number;
  cameraId?: string,
  microphoneId?: string;
  channelCount?: number;
  useExactDeviceId?: boolean;
  retryWhenExactFailed?: boolean;
  maxResolution?: boolean;
  // 是否使用最简单的 audio: true, video: true 作为 constraint
  useTrueAsConstraint?: boolean;
  useDeviceIdOnly?: boolean;
  echoCancellation?: boolean | string;
}

export interface BaseCaptureOption extends GetUserMediaOption {
  customSource?: MediaStreamTrack;
}

export interface CaptureOption {
  deviceId?: string;
  facingMode?: string;
  // 第一次采集和切换设备都用 exact deviceId 描述
  useExactDeviceId?: boolean;
  // 第一次采集 exact deviceId 失败后，去掉 exact 重试。切换设备 exact deviceId 失败不重试。
  retryWhenExactFailed?: boolean;
  customSource?: MediaStreamTrack;
  contentHint?: VideoContentHint;
}

export interface ScreenCaptureOption extends CaptureOption {
  systemAudio?: boolean;
  echoCancellation?: boolean;
  autoGainControl?: boolean;
  noiseSuppression?: boolean;
  audioTrack?: MediaStreamTrack;
  videoTrack?: MediaStreamTrack;
  captureElement?: HTMLElement;
  preferDisplaySurface?: 'current-tab' | 'tab' | 'window' | 'monitor';
  selfBrowserSurface?: 'include' | 'exclude';

}
interface encodedFrame {
  data: ArrayBuffer;
  readonly type?: 'key' | 'delta' | 'empty';
  readonly timestamp: number;
}
/** @internal */
export interface ILocalTrack extends ITrack {
  deviceId?: string; // 当前采集设备ID
  profile: VideoProfile | AudioProfile;
  isPublished: boolean;
  isPublishing: boolean;
  enableEncodeFrame: boolean;
  retryEncodeFailed?: (args: any) => any;
  isUseCustomSource: boolean;
  stat: LocalAudioStat | LocalVideoStat;
  encodeFrame?(data: RTCEncodedAudioFrame | RTCEncodedVideoFrame, small?: boolean): RTCEncodedAudioFrame | RTCEncodedVideoFrame;
  setProfile(profile: VideoProfile | AudioProfile): void;

  capture(captureOption: BaseCaptureOption | CaptureOption): void; // 开始采集

  publish(room: IRoom, promise: Promise<any>): Promise<any>; // 进入发布状态
  unpublish(): void; // 离开发布状态
}

export type IEncodeFrame<T extends AudioOrVideo> = T extends 'audio' ? RTCEncodedAudioFrame : RTCEncodedVideoFrame;
/** @internal */
export interface IRemoteTrack<T extends AudioOrVideo = AudioOrVideo> extends ITrack {
  userId: string;
  tinyId: string;
  decodeFrame(data: IEncodeFrame<T>): IEncodeFrame<T> | undefined;
  jitterBufferDelay: number;
  subscribe(promise: Promise<any>): void; // 进入订阅状态
  unsubscribe(): void; // 离开订阅状态
  get isRemotePublished(): boolean;
  get isSubscribing(): boolean;
  get isSubscribed(): boolean;
  get streamType(): RemoteStreamType;
  get isAvailable(): boolean;
  waitHasMediaTrack(): Promise<void>;
  setMediaType(mediaType: MediaType): void;
}
export const enum VideoPlayerMode {
  ELEMENT,
  CANVAS_FROM_ELEMENT,
  CANVAS_WITHOUT_ELEMENT,
}
export const enum AudioPlayerMode {
  ELEMENT,
  CONTEXT,
}
export const enum BannedReason {
  BANNED = 'banned',
  KICK = 'kick',
  USER_TIME_OUT = 'user_time_out',
  ROOM_DISBAND = 'room_disband',
}
export const enum LeaveReason {
  USER_EXIT_REASON_TC_USER_EXIT_NORMAL, // 正常退出
  USER_EXIT_REASON_TC_USER_EXIT_TIMEOUT, // 由于超时退出
  USER_EXIT_REASON_TC_USER_EXIT_KICKED, // 被踢
  USER_EXIT_REASON_TC_USER_EXIT_CHANGED, // 角色切换
  USER_KICK_OUT_CODE_BUSINESS_USER, // 第三方业务后台发起踢人
  USER_KICK_OUT_CODE_BUSINESS_ROOM, // 第三方业务后台发起解散房间
  USER_KICK_OUT_CODE_SERVER_USER, // 流控服务器主动发起踢人
  USER_KICK_OUT_CODE_SERVER_ROOM, // 流控服务器主动发起解散房间
  USER_KICK_SESS_EXSIT, // 用户已经在其他地方登录
}

export interface BandWidthOption { bandwidth: number, type: 'audio' | 'video', videoType?: 'big' | 'small' | 'auxiliary', sdp?: string; }

export const AUDIO_LEVEL_SCALE = 100000000;

export type AudioSourceOptions = {
  id: string;
  loop?: boolean;
  volume?: number;
  playbackRate?: number;
  onDurationChange?: (duration: number) => void;
  onTimeUpdate?: (currentTime: number, durationTime: number) => void;
  onEnded?: () => void;
} & ({
  url: string;
  track?: never;
} | {
  url?: never;
  track: MediaStreamAudioTrack;
});

export interface UpdateAudioSourceOptions {
  id: string;
  loop?: boolean;
  volume?: number;
  seekFrom?: number;
  operation?: 'pause' | 'resume' | 'stop';
  playbackRate?: number;
  onDurationChange?: (duration: number) => void;
  onTimeUpdate?: (currentTime: number, durationTime: number) => void;
  onEnded?: () => void;
}

export interface DenoiserOptions {
  assetsPath?: string,
  startDump?: boolean;
}

export interface InitDenoiserOptions {
  assetsPath?: string,
  sdkAppId: number,
  userId: string,
  userSig: string,
  mode?: DenoiserMode;
  startDump?: boolean;
}

export enum DenoiserMode {
  NORMAL = 0,
  FAR_FIELD_REDUCTION = 1
}

export interface UpdateDenoiserOptions {
  mode: DenoiserMode
}

export interface InitAudioProcessorOptions {
  assetsPath?: string,
  sdkAppId: number,
  userId: string,
  userSig: string,
  audioReference?: AudioSource[];
  processLevel?: number;
  enableDump?: boolean;
  isLocalAudioNeedAudioProcess?: boolean;
  isScreenAudioNeedAudioProcess?: boolean;
}

export interface UpdateAudioProcessorOptions {
  audioReference?: AudioSource[]; // 非增量更新
  processLevel?: number;
  enableDump?: boolean;
}

export interface SSRC {
  audio: number;
  video: number;
  videoRtx: number;
  small?: number;
  smallRtx?: number;
  auxiliary: number;
  auxiliaryRtx: number;
}

export interface SSRCGroup {
  audioSsrc: number;
  bigVideoSsrc: number;
  bigVideoRtxSsrc: number;
  auxVideoSsrc: number;
  auxVideoRtxSsrc: number;
}

export class SubscribeMediaType {
  mediaType = MediaType.NULL;
  set audio(value: boolean) {
    if (value) this.mediaType |= MediaType.AUDIO;
    else this.mediaType &= ~MediaType.AUDIO;
  }
  get audio(): boolean {
    return !!(this.mediaType & MediaType.AUDIO);
  }

  set video(value: boolean) {
    if (value) this.mediaType |= MediaType.BIG_VIDEO;
    else this.mediaType &= ~MediaType.BIG_VIDEO;
  }
  get video(): boolean {
    return !!(this.mediaType & MediaType.BIG_VIDEO);
  }
  set auxiliary(value: boolean) {
    if (value) this.mediaType |= MediaType.AUX_VIDEO;
    else this.mediaType &= ~MediaType.AUX_VIDEO;
  }
  get auxiliary(): boolean {
    return !!(this.mediaType & MediaType.AUX_VIDEO);
  }
  set smallVideo(value: boolean) {
    if (value) this.mediaType |= MediaType.SMALL_VIDEO;
    else this.mediaType &= ~MediaType.SMALL_VIDEO;
  }
  get smallVideo(): boolean {
    return !!(this.mediaType & MediaType.SMALL_VIDEO);
  }
}
export const enum BASIC_TYPE {
  String = 'string',
  Number = 'number',
  Boolean = 'boolean',
  Array = 'array',
  Object = 'object'
}
// CDN Streaming
export interface StartMixTranscodeData {
  roomId: string;
  mcuRequestTime: number;
  outputSessionId: string;
  inputParam: {
    userId: string;
    roomId: string;
    width: number;
    height: number;
    locationX: number;
    locationY: number;
    zOrder: number | undefined;
    streamType: number;
    inputType: number;
    renderMode: number;
  }[];
  outputParam: {
    streamId: string;
    streamType: number;
    width: number;
    height: number;
    videoBps: number;
    fps: number;
    gop: number;
    audioSampleRate: number;
    audioBps: number;
    audioChannels: 1 | 2;
    backgroundColor: number;
    backgroundImg: string;
    extraInfo: string;
    videoCodec: number;
    audioCodec: number;
  };
}

export interface StopMixTranscodeData {
  mcuRequestTime: number;
  outputSessionId: string;
  streamType: number;
}

export interface StartPublishTencentCDNData {
  requestTime: number;
  sessionId: string;
  streamId: string;
  streamType: number;
}
export interface StartPublishGivenCDNData {
  pushRequestTime: number;
  pushAppId: number;
  pushBizId: number;
  pushCdnUrl: string;
  pushStreamType: 'main' | 'aux';
  pushStreamId: string | undefined;
}

export type StopPublishTencentCDNData = Omit<StartPublishTencentCDNData, 'streamId' | 'streamType'>;
export type StopPublishGivenCDNData = Omit<StartPublishGivenCDNData, 'pushStreamId'>;

export interface AbilityStatusReportData {
  scheduleCache?: number;
  ai_denoise?: number;
  '2k4k'?: number;
  sched_domain?: string;
  sched_back_domain?: string;
  signal_domain?: string;
  signal_back_domain?: string;
}


export interface CustomMessageData {
  cmdId: number;
  data: ArrayBuffer;
}
export interface CustomMessage extends CustomMessageData {
  userId: string;
  seq: number;
}

export enum VideoCodec {
  H264 = 'h264',
  H265 = 'h265',
  VP8 = 'vp8',
  VP9 = 'vp9',
  AV1 = 'av1'
}

export const enum VideoCodecPipelineType {
  ENCRYPT_AND_DECRYPT,
  DUMP,
  SEI,
  ENCODE_AND_DECODE
}
export const enum AudioCodecPipelineType {
  ENCRYPT_AND_DECRYPT,
  NTP_TO_AUDIO_FRAME,
  DUMP,
  ENCODE_AND_DECODE
}
// 新混流转推信令的类型 https://iwiki.woa.com/p/1109917349#%E4%BA%8C%E5%8D%81%E5%9B%9B%E3%80%81%E6%96%B0%E6%B7%B7%E6%B5%81%E8%BD%AC%E6%8E%A8%E4%BF%A1%E4%BB%A4

export interface StartPublishMixStreamToRoomData {
  sdkappid: number; // 必填，必须和主播所在房间一致
  roomid: string; // 发起转推任务的房间ID，必须和主播所在房间一致
  roomType: number; // 发起转推任务的房间类型，0为整形房间号，1为字符串房间号，默认是整形房间号，必须和主播所在房间一致
  transcode: boolean; // 是否需要转码，混流必为 true, true 会收费用，文档里需要写明会收转码费用
  sessionId: string; // 任务防冲突判断，同一时间，同一个 sdkappid 不允许出现重名的转推任务，必填
  agentParam: AgentParam; // 伴生进房拉流/生命周期管理参数
  audioParam: AudioParam; // 音频编码和混音列表参数
  videoParam: VideoParam; // 视频编码和布局参数
  singleSubscribeParam?: SingleSubscribeParam; // 仅在单流旁路时设置此参数，transcode为false时必填
  publishCdnParams?: PublishCdnParam[]; // 转推CDN参数
  pushRtcRoomParams: PushRtcRoomParam[]; // 回推房间列表，有此参数表示回推房间，否则不回推
  enableNtpSync?: boolean; // 回推房间和推cdn是否需要精准同步，填了true，回推房间和推CDN都会做流之间的对齐，参与混流任何上行不带ntp的tlv，则不做对齐。
  sequenceNumber: number; // 保证同一个任务，每次更新请求中的SequenceNumber递增，防止请求乱序。
}

export interface UpdatePublishMixStreamToRoomData {
  sdkappid: number;
  taskid: string;
  transcode: boolean;
  audioParam: AudioParam;
  videoParam: VideoParam;
  singleSubscribeParam?: SingleSubscribeParam;
  publishCdnParams?: PublishCdnParam[];
  pushRtcRoomParams: PushRtcRoomParam[];
  enableNtpSync: boolean;
  sequenceNumber: number;
}

export interface StopPublishMixStreamToRoomData {
  sdkappid: number;
  taskid: string;
}

interface AgentParam {
  cdnRobotUserid: string; // 进房拉流的机器人 id，不要重复
  lifecycleUserid?: string;
  maxIdletime?: number;
}

export interface AudioParam {
  audioEncodeParam: {
    audioCodec?: number; // 可以不填
    audioSamplerate: number;
    audioChannels: number;
    audioBitrateKbps: number;
  };
  mixAudioUsers: MixAudioUser[];  // 不填为混所有人，需要在文档中说明
}

interface MixAudioUser {
  roomid?: string;
  roomType?: number;
  userid: string;
}

export interface VideoParam {
  videoEncodeParam: VideoEncodeParam;
  layoutParams: LayoutParam[];
  watermarkParams?: WatermarkParam[];
  backgroundColor: number;
  backgroundImageUrl: string;
}

interface VideoEncodeParam {
  videoCodec?: number; // 后台默认为 2，端上可以不填
  videoWidth: number;
  videoHeight: number;
  videoFramerate: number;
  videoGop: number;
  videoBitrateKbps: number;
}

interface LayoutParam {
  userMediaStream: UserMediaStream;
  imageWidth: number;
  imageHeight: number;
  locationX: number;
  locationY: number;
  imageZorder: number;
  renderMode: number;
  backgroundColor?: number; // 老接口没有
  backgroundImageUrl?: string; // 老接口没有
  enableStreamSei?: boolean; // 老接口没有
}

interface UserMediaStream {
  userInfo: UserInfo;
  streamType: number;
  mediaIndex?: number;
}

interface UserInfo {
  roomid?: string;
  roomType?: number;
  userid: string;
}

interface WatermarkParam {
  type: number;
  picWatermark: PicWatermark;
}

interface PicWatermark {
  watermarkUrl: string;
  width: number;
  height: number;
  locationX: number;
  locationY: number;
  zorder: number;
  alpha: number;
}

interface SingleSubscribeParam {
  userInfo: UserInfo;
  streamType: number;
  mediaIndex: number;
}

interface PublishCdnParam {
  tencentCdn: boolean;
  publishCdnUrl: string;
}

export interface PushRtcRoomParam {
  roomid: string;
  roomType: number;
  pushRobotUserid: string;
}


export enum CodecType {
  WebRTC = 'webrtc',
  WebCodecs = 'webcodecs',
  WebAssembly = 'webassembly'
}


export const enum DECODE_FAILED_ERROR_CODE {
  SUCCESS = 0,
  FAILED = 1,
  WEBCODEC_INIT = 2,
  WEBCODEC_CONFIG_NOT_SUPPORT = 3,
  WEBCODEC_DECODER_ERROR = 4,
  WEBCODEC_TRACK_MUTE = 5,
  WASM_INIT = 6,
  WASM_WEBGL_UNAVALIABLE = 7,
  WASM_DECODER_ERROR = 8,
  WASM_TRACK_MUTE = 9,
  TEST = 10,
  RENDER_2D_ERROR,
}
export enum VideoContentHint {
  NONE = '',
  DETAIL = 'detail',
  MOTION = 'motion',
  TEXT = 'text'
}
export enum TIMER_TYPE {
  INTERVAL = 'interval',
  TIMEOUT = 'timeout',
  RAF = 'raf',
  RIC = 'ric',
  INTERVAL_IN_WORKER = 'intervalInWorker'
}

export interface PlayoutDelay {
  min: number;
  max: number;
}

export interface JitterBufferDelay {
  main?: number;
  aux?: number;
}

export const enum SMALL_MODE {
  CANVAS = 'canvas',
  API = 'api',
}

export const enum CheckPermissionType {
  NONE = 0,
  MICROPHONE,
  CAMERA,
  BOTH
}

export const enum DeviceType {
  CAMERA = 'camera',
  MICROPHONE = 'microphone'
}

export enum AlphaStitchingType {
  none = 0b0,
  horizontal = 0b1,
  vertical = 0b10,
}

export enum LatencyLevel {
  LOW = 1, // 默认值，低延迟采用优质线路
  NORMAL = 2 // 普通延迟，采用边缘节点，设置后计费模式会变成极速直播计费。
}
